requestAnimationFrame(() => {
  document.addEventListener("alpine:init", () => {
    Alpine.data("xMultipleStores", (sectionId = "") => ({
      active: 1,
      open: false,
      canScroll: false,
      atTop: true,
      atBottom: false,
      showStore: false,
      sectionId,
      mapCenter: { lat: 13.02, lng: 80.21 },
      mapZoom: 17,
      mapReady: false,
      mapOverview: false,
      storeLocations: [],
      scrollEndTimer: null,
      isScrollUpdating: false,
      googleMap: null,
      googleMarker: null,
      googleInfoWindow: null,
      googleMapsScriptPromise: null,

      get mapsApiKey() {
        const root = this.$el?.closest?.("[data-google-maps-key]") || document.getElementById(`shopify-section-${this.sectionId}`);
        return root?.dataset?.googleMapsKey || "";
      },

      get activeStore() {
        return this.storeLocations.find((item) => item.index === this.active) || {};
      },

      get activeStoreBrand() {
        return this.activeStore.brand || this.activeStore.query || "Store";
      },

      get activeStoreArea() {
        return this.activeStore.area || "";
      },

      get activeStoreLabel() {
        return this.activeStore.heading || this.activeStoreBrand;
      },

      get activeStoreAddress() {
        const store = this.activeStore;
        return store.query || store.address || this.activeStoreArea || "";
      },

      get activeStoreHours() {
        return this.activeStore.hours || "";
      },

      get activeStoreButtonLabel() {
        return this.activeStore.buttonLabel || "Get directions";
      },

      get activeMapLink() {
        const store = this.activeStore;
        if (store.link) return store.link;
        if (store.query) {
          return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(store.query)}`;
        }
        if (Number.isFinite(store.lat) && Number.isFinite(store.lng)) {
          return `https://www.google.com/maps/search/?api=1&query=${store.lat},${store.lng}`;
        }
        return "";
      },

      get activeDirectionsLink() {
        const store = this.activeStore;
        if (store.link) return store.link;
        const destination =
          store.query ||
          (Number.isFinite(store.lat) && Number.isFinite(store.lng)
            ? `${store.lat},${store.lng}`
            : "");
        if (destination) {
          return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
        }
        return "";
      },

      get canZoomIn() {
        return this.mapZoom < 19;
      },

      get canZoomOut() {
        return this.mapZoom > 10;
      },

      load() {
        this.initStoreLocations();
        this.loadStoreThumbnails();
        this.syncScrollState();
        window.addEventListener("resize", () => {
          this.heightNatural();
          if (this.googleMap && window.google?.maps) {
            google.maps.event.trigger(this.googleMap, "resize");
            const store = this.activeStore;
            if (store && Number.isFinite(store.lat) && Number.isFinite(store.lng)) {
              this.googleMap.setCenter({ lat: store.lat, lng: store.lng });
            }
          }
          if (this.mapOverview) this.updateMarkerPositions();
          this.syncScrollState();
        });
        this.heightNatural();
        this.$nextTick(() => {
          this.focusMapOnStore(this.active);
        });
      },

      initStoreLocations() {
        const root = this.$refs.list_stores;
        if (!root) return;

        this.storeLocations = Array.from(root.querySelectorAll("[data-store-index]")).map((el) => ({
          index: Number(el.dataset.storeIndex),
          lat: parseFloat(el.dataset.storeLat),
          lng: parseFloat(el.dataset.storeLng),
          query: el.dataset.storeQuery || "",
          link: el.dataset.storeLink || "",
          brand: el.dataset.storeBrand || "",
          area: el.dataset.storeArea || "",
          heading: el.dataset.storeHeading || "",
          address: el.dataset.storeAddress || "",
          hours: el.dataset.storeHours || "",
          buttonLabel: el.dataset.storeButtonLabel || "",
          googleImage: el.dataset.storeGoogleImage || "",
        }));
      },

      loadStoreThumbnails() {
        const root = this.$refs.list_stores;
        if (!root) return;

        root.querySelectorAll(".store-card-thumb-js").forEach((img) => {
          const googleImage = img.dataset.googleImage;
          const lat = parseFloat(img.dataset.lat);
          const lng = parseFloat(img.dataset.lng);
          const query = img.dataset.query || "";
          const key = this.mapsApiKey;
          const thumbWrap = img.parentElement;
          const fallback = thumbWrap?.querySelector(".store-card-thumb-fallback");

          const showFallback = () => {
            img.remove();
            if (fallback) fallback.hidden = false;
          };

          if (googleImage) {
            img.src = googleImage;
            img.onload = () => img.classList.add("store-card-thumb-loaded");
            img.onerror = showFallback;
            return;
          }

          if (key && Number.isFinite(lat) && Number.isFinite(lng)) {
            img.src = `https://maps.googleapis.com/maps/api/streetview?size=200x150&location=${lat},${lng}&fov=80&heading=70&pitch=0&key=${encodeURIComponent(key)}`;
            img.onload = () => img.classList.add("store-card-thumb-loaded");
            img.onerror = () => {
              if (query) {
                img.src = `https://maps.googleapis.com/maps/api/streetview?size=200x150&location=${encodeURIComponent(query)}&fov=80&heading=70&pitch=0&key=${encodeURIComponent(key)}`;
                img.onerror = showFallback;
              } else {
                showFallback();
              }
            };
            return;
          }

          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            img.src = `https://staticmap.openstreetmap.de/staticmap.php?center=${lat},${lng}&zoom=16&size=200x150&markers=${lat},${lng}`;
            img.onload = () => img.classList.add("store-card-thumb-loaded");
            img.onerror = showFallback;
            return;
          }

          showFallback();
        });
      },

      selectStore(index, options = {}) {
        const next = Number(index);
        if (next === this.active && this.mapReady && !options.force) return;
        this.active = next;
        if (!options.fromScroll) {
          this.scrollStoreIntoView(this.active);
        }
        this.focusMapOnStore(this.active);
      },

      updateActiveFromScroll() {
        const root = this.$refs.list_stores;
        if (!root) return;

        const isHorizontal = getComputedStyle(root).flexDirection === "row";
        if (!isHorizontal) return;

        const rootRect = root.getBoundingClientRect();
        const centerX = rootRect.left + rootRect.width / 2;
        let bestIndex = this.active;
        let bestDistance = Infinity;

        root.querySelectorAll("[data-store-index]").forEach((el) => {
          const rect = el.getBoundingClientRect();
          const itemCenter = rect.left + rect.width / 2;
          const distance = Math.abs(itemCenter - centerX);
          if (distance < bestDistance) {
            bestDistance = distance;
            bestIndex = Number(el.dataset.storeIndex);
          }
        });

        if (bestIndex === this.active) return;

        this.isScrollUpdating = true;
        this.active = bestIndex;
        clearTimeout(this.scrollEndTimer);
        this.scrollEndTimer = setTimeout(() => {
          this.focusMapOnStore(this.active);
          this.isScrollUpdating = false;
        }, 120);
      },

      buildMapEmbedUrl(store, zoom = 17) {
        const key = this.mapsApiKey;
        const place =
          store.query ||
          store.heading ||
          (Number.isFinite(store.lat) && Number.isFinite(store.lng)
            ? `${store.lat},${store.lng}`
            : "");

        if (key && place) {
          return `https://www.google.com/maps/embed/v1/place?key=${encodeURIComponent(key)}&q=${encodeURIComponent(place)}&zoom=${zoom}&maptype=roadmap`;
        }

        if (Number.isFinite(store.lat) && Number.isFinite(store.lng)) {
          return `https://maps.google.com/maps?q=${store.lat},${store.lng}&ll=${store.lat},${store.lng}&z=${zoom}&t=m&ie=UTF8&hl=en&output=embed`;
        }

        if (store.query) {
          return `https://maps.google.com/maps?q=${encodeURIComponent(store.query)}&z=${zoom}&t=m&ie=UTF8&hl=en&output=embed`;
        }

        return "about:blank";
      },

      escapeHtml(value) {
        return String(value ?? "")
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;");
      },

      loadGoogleMapsScript() {
        if (!this.mapsApiKey) {
          return Promise.reject(new Error("Missing Google Maps API key"));
        }

        if (window.google?.maps) {
          return Promise.resolve();
        }

        if (!this.googleMapsScriptPromise) {
          this.googleMapsScriptPromise = new Promise((resolve, reject) => {
            const existing = document.querySelector('script[data-stores-google-maps="1"]');
            if (existing) {
              existing.addEventListener("load", () => resolve(), { once: true });
              existing.addEventListener("error", () => reject(new Error("Google Maps failed to load")), { once: true });
              return;
            }

            const script = document.createElement("script");
            script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(this.mapsApiKey)}&v=weekly`;
            script.async = true;
            script.defer = true;
            script.dataset.storesGoogleMaps = "1";
            script.onload = () => resolve();
            script.onerror = () => reject(new Error("Google Maps failed to load"));
            document.head.appendChild(script);
          });
        }

        return this.googleMapsScriptPromise;
      },

      async renderGoogleMap(store) {
        const canvas = this.$refs.google_map_canvas;
        if (!canvas) return;

        if (!Number.isFinite(store.lat) || !Number.isFinite(store.lng)) {
          const iframe = this.$refs.map_iframe;
          if (iframe) {
            this.mapReady = false;
            const url = this.buildMapEmbedUrl(store, this.mapZoom);
            if (url !== "about:blank") iframe.src = url;
          }
          return;
        }

        try {
          await this.loadGoogleMapsScript();
        } catch {
          const iframe = this.$refs.map_iframe;
          if (iframe) {
            this.mapReady = false;
            const url = this.buildMapEmbedUrl(store, this.mapZoom);
            if (url !== "about:blank") iframe.src = url;
          }
          return;
        }

        const center = { lat: store.lat, lng: store.lng };
        const title = store.heading || store.brand || "Store";
        const address = store.query || store.address || "";
        const hours = store.hours || "";
        const directionsLabel = store.buttonLabel || "Get directions";
        const directionsUrl =
          store.link ||
          (store.query
            ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(store.query)}`
            : `https://www.google.com/maps/search/?api=1&query=${store.lat},${store.lng}`);

        if (!this.googleMap) {
          this.googleMap = new google.maps.Map(canvas, {
            center,
            zoom: this.mapZoom,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
          });
          this.googleInfoWindow = new google.maps.InfoWindow({ maxWidth: 320 });
        }

        this.googleMap.setCenter(center);
        this.googleMap.setZoom(this.mapZoom);

        if (this.googleMarker) {
          this.googleMarker.setMap(null);
        }

        this.googleMarker = new google.maps.Marker({
          map: this.googleMap,
          position: center,
          title,
          animation: google.maps.Animation.DROP,
        });

        let infoHtml = '<div class="stores-google-info">';
        infoHtml += `<div class="stores-google-info__title">${this.escapeHtml(title)}</div>`;
        if (address) {
          infoHtml += `<div class="stores-google-info__address">${this.escapeHtml(address)}</div>`;
        }
        if (hours) {
          infoHtml += `<div class="stores-google-info__hours">${this.escapeHtml(hours)}</div>`;
        }
        infoHtml += `<a class="stores-google-info__link" href="${directionsUrl}" target="_blank" rel="noopener noreferrer">${this.escapeHtml(directionsLabel)}</a>`;
        infoHtml += "</div>";

        this.googleInfoWindow.setContent(infoHtml);
        this.googleInfoWindow.open(this.googleMap, this.googleMarker);

        await this.$nextTick();
        requestAnimationFrame(() => {
          if (!this.googleMap) return;
          google.maps.event.trigger(this.googleMap, "resize");
          this.googleMap.setCenter(center);
          if (this.googleMarker) {
            this.googleInfoWindow.open(this.googleMap, this.googleMarker);
          }
        });

        this.mapReady = true;
      },

      focusMapOnStore(index) {
        const store = this.storeLocations.find((item) => item.index === Number(index));
        if (!store) return;

        this.mapOverview = false;
        this.mapZoom = 17;

        if (Number.isFinite(store.lat) && Number.isFinite(store.lng)) {
          this.mapCenter = { lat: store.lat, lng: store.lng };
        }

        if (this.mapsApiKey && this.$refs.google_map_canvas) {
          this.mapReady = false;
          this.renderGoogleMap(store);
          return;
        }

        const iframe = this.$refs.map_iframe;
        if (iframe) {
          this.mapReady = false;
          const url = this.buildMapEmbedUrl(store, this.mapZoom);
          if (url !== "about:blank") iframe.src = url;
          return;
        }

        this.mapReady = true;
      },

      onMapIframeLoad() {
        const iframe = this.$refs.map_iframe;
        if (!iframe || !iframe.src || iframe.src === "about:blank") return;
        this.mapReady = true;
        if (this.mapOverview) {
          this.$nextTick(() => this.updateMarkerPositions());
        }
      },

      zoomMap(delta) {
        const store = this.activeStore;

        const nextZoom = Math.min(19, Math.max(10, this.mapZoom + Number(delta)));
        if (nextZoom === this.mapZoom) return;

        this.mapZoom = nextZoom;

        if (this.googleMap) {
          this.googleMap.setZoom(this.mapZoom);
          return;
        }

        const iframe = this.$refs.map_iframe;
        if (!store || !iframe) return;

        this.mapReady = false;
        const url = this.buildMapEmbedUrl(store, this.mapZoom);
        if (url !== "about:blank") iframe.src = url;
      },

      scrollStoreIntoView(index) {
        const root = this.$refs.list_stores;
        if (!root) return;
        const item = root.querySelector(`[data-store-index="${index}"]`);
        if (!item) return;

        const isHorizontal = getComputedStyle(root).flexDirection === "row";
        if (isHorizontal) {
          root.scrollTo({ left: item.offsetLeft, behavior: "smooth" });
        } else {
          root.scrollTo({
            top: item.offsetTop - root.offsetTop,
            behavior: "smooth",
          });
        }
        requestAnimationFrame(() => this.syncScrollState());
      },

      latLngToPoint(lat, lng, centerLat, centerLng, zoom, width, height) {
        const worldSize = 256 * Math.pow(2, zoom);
        const project = (latitude, longitude) => {
          const x = ((longitude + 180) / 360) * worldSize;
          const sinLat = Math.sin((latitude * Math.PI) / 180);
          const y = (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * worldSize;
          return { x, y };
        };

        const center = project(centerLat, centerLng);
        const point = project(lat, lng);
        return {
          left: width / 2 + (point.x - center.x),
          top: height / 2 + (point.y - center.y),
        };
      },

      updateMarkerPositions() {
        const map = this.$refs.stores_map;
        if (!map || !this.storeLocations.length || !this.mapOverview) return;

        const width = map.clientWidth;
        const height = map.clientHeight;
        if (!width || !height) return;

        this.storeLocations.forEach((store) => {
          const marker = map.querySelector(`[data-marker-index="${store.index}"]`);
          if (!marker || !Number.isFinite(store.lat) || !Number.isFinite(store.lng)) return;

          const point = this.latLngToPoint(
            store.lat,
            store.lng,
            this.mapCenter.lat,
            this.mapCenter.lng,
            this.mapZoom,
            width,
            height
          );

          const inBounds = point.left >= -40 && point.left <= width + 40 && point.top >= -40 && point.top <= height + 40;
          marker.style.left = `${point.left}px`;
          marker.style.top = `${point.top}px`;
          marker.style.display = inBounds ? "" : "none";
        });
      },

      syncScrollState() {
        const root = this.$refs.list_stores;
        if (!root) return;

        const isHorizontal = getComputedStyle(root).flexDirection === "row";
        if (isHorizontal) {
          this.atTop = root.scrollLeft <= 2;
          this.atBottom = root.scrollLeft + root.clientWidth >= root.scrollWidth - 2;
          this.updateActiveFromScroll();
        } else {
          this.atTop = root.scrollTop <= 2;
          this.atBottom = root.scrollTop + root.clientHeight >= root.scrollHeight - 2;
        }

        this.canScroll = root.scrollWidth > root.clientWidth + 2 || root.scrollHeight > root.clientHeight + 2;

        const scrollWrap = this.$refs.stores_list_scroll_wrap;
        if (scrollWrap && window.matchMedia("(min-width: 768px)").matches) {
          scrollWrap.classList.toggle("is-not-scrollable", !this.canScroll);
          scrollWrap.classList.toggle("is-scrolled-end", this.canScroll && this.atBottom);
        }
      },

      heightNatural() {
        if (window.matchMedia("(min-width: 768px)").matches) {
          if (this.$refs.natural_height && this.$refs.h_img_location) {
            this.$refs.natural_height.style.height = this.$refs.h_img_location.offsetHeight + "px";
          }
        } else if (this.$refs.natural_height) {
          this.$refs.natural_height.style.removeProperty("height");
        }
      },

      openLocation(el) {
        this.open = true;
        const popupContent = document.getElementById(el.getAttribute("data-id"));
        this.$refs.content_location_detail.innerHTML = popupContent.innerHTML;
        const title = this.$refs.content_location_detail.querySelector("h5.location-title");
        if (title) {
          const h4 = document.createElement("h4");
          h4.innerHTML = title.innerHTML;
          h4.className = title.className;
          title.replaceWith(h4);
        }
      },

      hideLocation() {
        requestAnimationFrame(() => {
          this.open = false;
          Alpine.store("xPopup").open = false;
        });
      },

      scrollUp() {
        this.scrollByDirection(-1);
      },

      scrollDown() {
        this.scrollByDirection(1);
      },

      scrollByDirection(direction) {
        const root = this.$refs.list_stores;
        if (!root) return;

        const isHorizontal = getComputedStyle(root).flexDirection === "row";
        if (isHorizontal) {
          root.scrollBy({ left: direction * 260, behavior: "smooth" });
        } else {
          root.scrollBy({ top: direction * 200, behavior: "auto" });
        }
        requestAnimationFrame(() => this.syncScrollState());
      },

      checkCanScrollVertical() {
        this.syncScrollState();
      },

      toggleStore(noScroll = false) {
        this.showStore = !this.showStore;
        if (!this.showStore) {
          this.$refs.first_store.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        if (!noScroll) {
          this.canScroll = !this.canScroll;
          this.$refs.list_stores.addEventListener("animationend", this.syncScrollState());
          setTimeout(() => {
            this.$refs.list_stores.removeEventListener("animationend", this.syncScrollState());
          }, 300);
        }
      },
    }));
  });
});
